import { createContext, useContext } from "react";
// Imported from "react-router" directly, not "react-router-dom": __RouterContext is internal and
// react-router-dom doesn't re-export it (see this file's doc comment for why it's needed anyway).
// `useGraphTrip.ts` already imports a hook straight from "react-router" for the same reason.
import { __RouterContext } from "react-router";
import { MarketingLangCode, stripLangPrefix } from "./urlLocale";

/**
 * Set exactly once, by `MarketingShell.tsx`, from the same `stripLangPrefix(location.pathname)`
 * call every marketing route component already trusts. `null` (not `"en"`) is the "nothing
 * provided" sentinel — every real `MarketingLangCode`, including `"en"`, is a non-empty string
 * and therefore truthy, so a plain truthiness check in `useMarketingLang` below can't confuse a
 * resolved English page with an absent provider.
 */
export const MarketingLangContext = createContext<MarketingLangCode | null>(null);

/**
 * The locale for a marketing-site component that is not itself a routed page — those get `lang`
 * as a prop straight from `MarketingShell.tsx` (see `guideRoutes.tsx`'s doc comment on why that
 * prop has to be forwarded explicitly). Everything else — nav, footer, homepage sections, cards —
 * sits under `MarketingShell` without a prop chain reaching it, and resolves in three tiers, each
 * a fallback for when the one above it isn't there:
 *
 *  1. `MarketingLangContext` — provided by `MarketingShell` from its own `stripLangPrefix` call.
 *     This is what makes a deeply-nested component (e.g. a card three components below the page)
 *     agree with the page around it without every intermediate component threading a `lang` prop
 *     it has no other use for.
 *
 *  2. React-router's own location, read directly off `react-router`'s internal `__RouterContext`
 *     rather than through the public `useLocation()` hook. `useLocation()` calls
 *     `useContext(context).location` unconditionally, which throws when no `<Router>` is an
 *     ancestor — `context`'s default value is `undefined`, so `.location` reads off `undefined`.
 *     This hook has to stay callable with no Router above it too (see tier 3), so it reads the
 *     context value itself and checks it before touching `.location`. `StaticRouter` (server,
 *     `entry-server.tsx`) and `BrowserRouter` (client, `entry-client.tsx`) both populate this
 *     context with the real requested URL, so this tier is what gives server and client the same
 *     answer on the very first render — no `useEffect`, no second paint, no #418/#423 hydration
 *     mismatch. This is the fix: `MarketingShell.tsx:69` already derives its own `lang` this way
 *     (via `useLocation()`, safe there because `MarketingShell` is always rendered inside a
 *     Router), and every one of the 22 call sites this hook replaces used to read `window` first,
 *     which does not exist yet on the server and silently resolved to `"en"`.
 *
 *  3. `typeof window === "undefined" ? "en" : stripLangPrefix(window.location.pathname).lang` —
 *     the exact fallback every one of those call sites used before this hook existed. Kept so a
 *     component rendered with neither a context provider nor a Router above it — most of today's
 *     Jest unit tests render bare, with no wrapper at all — behaves exactly as it did before this
 *     hook existed. Do not remove this tier to "simplify": it is load-bearing for every existing
 *     test that renders one of these components without a `<Router>`.
 */
export function useMarketingLang(): MarketingLangCode {
	const providedLang = useContext(MarketingLangContext);
	const routerState = useContext(__RouterContext) as { location?: { pathname: string } } | undefined;

	if (providedLang) return providedLang;
	if (routerState?.location) return stripLangPrefix(routerState.location.pathname).lang;
	return typeof window === "undefined" ? "en" : stripLangPrefix(window.location.pathname).lang;
}
